import { useCallback, useEffect } from "react";
import Patient from "../../../Utility/Crud/Patient";
import User from "../../../Utility/Crud/User";
import QueueRoute from "../../../Utility/Crud/QueueRoute";

/**
 * Encapsulates create-order draft building, reconciliation with the chart,
 * navigation into ticket routes, and route-driven effects (refresh sync and
 * invalid draft redirect).
 *
 * @param {Object} parameters
 * @param {Object|null} parameters.chart - Current chart state.
 * @param {Object|undefined} parameters.patient - Current patient record for the chart.
 * @param {number|null} parameters.patientId - Active patient identifier.
 * @param {Object} parameters.mainContext - Main application context (users, current user).
 * @param {function(string): string} parameters.formatOrderType - Maps route order or ticket type strings to stored labels.
 * @param {function(Object): void} parameters.onChange - Merges chart fragment updates into chart state.
 * @param {function(): number} parameters.getNegativeId - Returns the next temporary negative entity identifier.
 * @param {number|null} parameters.orderId - Currently focused order or draft order identifier.
 * @param {function(number|null): void} parameters.setOrderId - Sets the focused order identifier.
 * @param {string|null} parameters.pageMode - Route-derived page mode segment.
 * @param {boolean} parameters.isChartLoading - True while the chart is loading from the server.
 * @param {string[]} parameters.ticketPageModes - Page mode values that represent create-ticket flows.
 * @param {function(string, Object|null): void} parameters.handleNavigateChart - Navigation helper with unsaved-change handling.
 * @param {function(): boolean} parameters.hasPendingChanges - Checks if chart has pending changes.
 * @param {boolean} parameters.setShowSaveDialog - Shows the save dialog if we have pending changes.
 * @param {string} parameters.setPendingNavigatePath - Sets the path to navigate to on dialog save.
 * @param {number|null} parameters.routeParentOrderId - Parent order id parsed directly from the route.
 * @returns {{ prepareTicketData: function(Object, string, Object|null): void }}
 */
export function useChartCreateOrderFlow({
  chart,
  patient,
  patientId,
  mainContext,
  formatOrderType,
  onChange,
  getNegativeId,
  orderId,
  setOrderId,
  pageMode,
  isChartLoading,
  orderPageModes,
  handleNavigateChart,
  hasPendingChanges,
  setShowSaveDialog,
  setPendingNavigatePath,
  routeParentOrderId,
}) {
  /**
   * Resolves the default marketing campaign for a newly created New order.
   * Falls back to the most recent prior activity with a non-null campaign.
   * @returns {number|null}
   */
  const getDefaultMarketingCampaignId = useCallback(() => {
    // Do not default a marketing campaign if the patient has any delivered New order.
    if (
      Object.values(chart.orderActivity).some(
        (orderActivity) =>
          orderActivity.orderType?.toLowerCase() === "new" &&
          orderActivity.setupDate != null,
      )
    ) {
      return null;
    }

    const previousNewOrders = Object.values(chart.orderActivity).sort(
      (a, b) => {
        const aStartedAt = new Date(chart.activity[a.id].startedAt).getTime();
        const bStartedAt = new Date(chart.activity[b.id].startedAt).getTime();

        if (aStartedAt !== bStartedAt) {
          return aStartedAt - bStartedAt;
        }

        return 0;
      },
    );

    for (let i = previousNewOrders.length - 1; i >= 0; i--) {
      const orderActivity = previousNewOrders[i];
      if (orderActivity.marketingCampaignId != null) {
        return orderActivity.marketingCampaignId;
      }
    }

    return null;
  }, [chart?.activity, chart?.orderActivity]);

  /**
   * Prepares the order data for the create order page.
   * @param {string} orderType - The type of order to create. "new" or "re-supply".
   */
  const buildCreateOrderData = useCallback(
    (orderType, parentOrder = null) => {
      const newActivityId = getNegativeId();
      const newApprovalId = getNegativeId();
      const newServiceOrderReasonId = parentOrder ? getNegativeId() : null;
      const isNewOrderFlow =
        orderType === "new" || orderType === "accessoryAfterSale";
      const ownerId =
        orderType === "service" || orderType === "resupply"
          ? 211
          : (Patient.getSalesRepUserprofile(chart, mainContext.usersIndexed)
              ?.id ??
            mainContext.currentUser?.id ??
            211);
      const newActivity = {
        [newActivityId]: {
          id: newActivityId,
          type: "Order",
          accountId: patient.accountId,
          startedAt: new Date().toISOString(),
          endedAt: new Date().toISOString(),
          quality: false,
          modified: new Date().toISOString(),
          priority: 0,
          isExpense: false,
          ownerId: ownerId,
        },
      };

      const location = Patient.getLocation(chart, mainContext.locationsIndexed);

      let status = null;
      let orderStatusReasonId = null;
      if (isNewOrderFlow) {
        const activityTypeEntry = Object.entries(
          mainContext.orderActivityTypesIndexed,
        ).find(([, value]) => value.name === formatOrderType(orderType));
        const activityTypeId = activityTypeEntry
          ? parseInt(activityTypeEntry[0])
          : null;
        const initialRoute = QueueRoute.getInitialRoute(
          mainContext.queueRoutesIndexed,
          activityTypeId,
        );
        orderStatusReasonId = initialRoute.toOrderStatusReasonId;
        status =
          mainContext.orderStatusReasonsIndexed[orderStatusReasonId]
            .orderStatus;
      }

      const newOrderActivity = {
        [newActivityId]: {
          id: newActivityId,

          type: "Order",
          quality: false,
          hasInfectiousDisease: false,
          priority: 0,
          startedAt: new Date(),
          lastStatusChange: new Date(),

          orderType: formatOrderType(orderType),
          status: status,
          orderStatusReasonId: orderStatusReasonId,

          insuranceTypeId: patient.insuranceTypeId,
          insuranceSubTypeId: patient.insuranceSubTypeId,
          insurancePolicy: patient.insurancePolicy,
          insuranceGroup: patient.insuranceGroup,

          secondaryInsuranceTypeId: patient.secondaryInsuranceTypeId,
          secondaryInsuranceSubTypeId: patient.secondaryInsuranceSubTypeId,
          secondaryInsurancePolicy: patient.secondaryInsurancePolicy,
          secondaryInsuranceGroup: patient.secondaryInsuranceGroup,

          lastUpdatedOn: new Date().toISOString(),
          lastUpdatedById: mainContext.currentUser.id,
          patientId: patientId,
          patientName: Patient.getDisplayName(patient),
          lastStatusChange: new Date().toISOString(),

          phone: patient.phone,
          address: patient.address,
          city: patient.city,
          state: patient.state,
          zipCode: patient.zipCode,
          dateOfBirth: patient.dateOfBirth,
          weight: patient.weight,
          gender: patient.gender,
          email: patient.email,
          firstDiagnosisCodeId: patient.firstDiagnosisCodeId,
          secondDiagnosisCodeId: patient.secondDiagnosisCodeId,
          thirdDiagnosisCodeId: patient.thirdDiagnosisCodeId,
          fourthDiagnosisCodeId: patient.fourthDiagnosisCodeId,
          orderingProviderId: patient.orderingProviderId,
          orderingProviderNotFound: patient.orderingProviderNotFound,
          jsonOrderingProviderNotFoundData:
            patient.jsonOrderingProviderNotFoundData,
          hasInfectiousDisease: patient.hasInfectiousDisease,
          diseaseNotes: patient.diseaseNotes,

          serviceLocationId: location?.id, // Handle in CardChartCreateOrder
          salesLocationId: location?.id, // Handle in CardChartCreateOrder
          billingLocationId: location?.billingLocationId, //TODO
          marketingCampaignId: isNewOrderFlow
            ? getDefaultMarketingCampaignId()
            : null,

          products: [], //TODO
        },
      };

      const approval = {
        [newApprovalId]: {
          id: newApprovalId,
        },
      };

      const orderActivityApprovals = {
        [newApprovalId]: {
          orderActivityId: newActivityId,
          approvalsId: newApprovalId,
        },
      };

      const serviceOrderReason = parentOrder
        ? {
            [newServiceOrderReasonId]: {
              id: newServiceOrderReasonId,
              previousOrderId: parentOrder.id,
              currentOrderId: newActivityId,
              name: Patient.getDisplayName(patient),
              technician: parentOrder.technician
                ? parentOrder.technician.name
                : "No Technician",
              salesRep: ownerId
                ? User.getDisplayName(mainContext.usersIndexed[ownerId])
                : "No Sales Rep",
              atp: parentOrder.atp ? parentOrder.atp.name : "No ATP",
              serviceReason: "",
            },
          }
        : null;

      return {
        createOrderId: newActivityId,
        chartChange: {
          activity: newActivity,
          orderActivity: newOrderActivity,
          ...(serviceOrderReason
            ? { serviceOrderReason: serviceOrderReason }
            : {}),
          approval: approval,
          orderActivityApprovals: orderActivityApprovals,
        },
      };
    },
    [
      chart,
      formatOrderType,
      getDefaultMarketingCampaignId,
      mainContext.currentUser?.id,
      mainContext.usersIndexed,
      mainContext.orderActivityTypesIndexed,
      mainContext.queueRoutesIndexed,
      mainContext.orderStatusReasonsIndexed,
      patient,
      patientId,
    ],
  );

  /**
   * Checks if there is a prior existing OrderId
   * otherwise goes through the create process.
   */
  const ensureCreateOrder = useCallback(
    (orderType, parentOrderId = null) => {
      const formattedOrderType = formatOrderType(orderType);
      const existingOrderId =
        Object.values(chart?.orderActivity || {}).find((orderActivity) => {
          if (
            !orderActivity ||
            orderActivity.id >= 0 ||
            orderActivity.type !== "Order" ||
            orderActivity.orderType !== formattedOrderType
          ) {
            return false;
          }

          if (parentOrderId === null) {
            return true;
          }

          return Object.values(chart?.serviceOrderReason || {}).some(
            (serviceReason) =>
              serviceReason?.currentOrderId === orderActivity.id &&
              serviceReason?.previousOrderId === parentOrderId,
          );
        })?.id ?? null;

      if (existingOrderId !== null) {
        return existingOrderId;
      }

      if (!patient) {
        return null;
      }

      const parentOrder =
        parentOrderId === null ? null : chart?.orderActivity?.[parentOrderId];
      if (parentOrderId !== null && !parentOrder) {
        return null;
      }

      const { createOrderId, chartChange } = buildCreateOrderData(
        orderType,
        parentOrder,
      );
      onChange(chartChange);

      return createOrderId;
    },
    [
      buildCreateOrderData,
      chart?.orderActivity,
      chart?.serviceOrderReason,
      formatOrderType,
      onChange,
      patient,
    ],
  );

  /**
   * Handles the creation of a new order.
   * @param {string} orderType new, re-supply
   */
  const prepareOrderData = (orderType, parentOrder = null) => {
    const path = parentOrder
      ? `/chart/${patientId}/order/${parentOrder.id}/${orderType}`
      : `/chart/${patientId}/${orderType}`;

    if (hasPendingChanges) {
      setPendingNavigatePath(path);
      setShowSaveDialog(true);
      return;
    }
    const createOrderId = ensureCreateOrder(orderType, parentOrder?.id ?? null);
    if (createOrderId !== null) {
      setOrderId(createOrderId);
    }

    handleNavigateChart(path);
  };

  /**
   * Handles creating an order on refresh.
   */
  useEffect(() => {
    if (
      isChartLoading ||
      !chart ||
      !patient ||
      !pageMode ||
      !orderPageModes.includes(pageMode) ||
      orderId < 0
    ) {
      return;
    }

    const createOrderOrderId = ensureCreateOrder(pageMode, routeParentOrderId);
    if (createOrderOrderId !== null && createOrderOrderId !== orderId) {
      setOrderId(createOrderOrderId);
    }
  }, [
    chart,
    ensureCreateOrder,
    isChartLoading,
    orderId,
    pageMode,
    patient,
    routeParentOrderId,
  ]);

  return {
    prepareOrderData,
  };
}
